///
/// EarthBrowser
///
/// Created by Matt Giger on 9/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Globe layer for story annotations (arcs, markers, region highlights).
/// Like PlacemarksLayer this is fed from redux rather than the dataset
/// DB: annotations belong to the story being played (dialog.activeStory)
/// or edited (dialog.storyEditorId). During playback only the active
/// stop's annotationIds are shown (with fade); the editor shows all of a
/// story's annotations so the author sees everything.
///
/// Also renders the click-to-draw preview (dialog.storyDrawing): placed
/// points as small domes plus a connecting line, so drawing an arc or
/// region reads immediately.
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { EmberDomeNode } from "@/earthbrowser/entities/EmberDome/EmberDomeNode"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { geographic } from "@/earthbrowser/utils/geographic"
import { type StoryAnnotation } from "@/redux/storiesSlice"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { trig } from "@stovokor/math/math"
import { matrix } from "@stovokor/math/matrix"
import { DatasetLayer, type DatasetFeature, type DatasetSceneObject } from "../DatasetLayer"
import { densifiedPathPositions, StoryAnnotationNode, storyPaletteColor } from "./StoryAnnotationNode"

export interface StoryAnnotationFeature extends DatasetFeature {
	id: string
	annotation: StoryAnnotation
	/** Whether the current stop (or the open editor) shows this annotation */
	shown: boolean
}

// Drawing preview styling (blue like the editor's drawing banner)
const cPreviewAltitudeKm = 3
const cPreviewPointRadiusKm = 0.4
const cPreviewMinAngular = 0.002
const cPreviewLineWidth = 2

/** In-progress click-to-draw geometry: point domes + connecting line */
class StoryDrawingPreviewNode extends NodeBase {
	constructor(context: EarthBrowserContext, kind: "arc" | "marker" | "region", points: { lat: number; lon: number }[]) {
		super()

		const previewColor = storyPaletteColor("blue", 0.9)
		for (const point of points) {
			const dome = new EmberDomeNode(context, previewColor)
			dome.minAngularSize = cPreviewMinAngular
			const holder = new NodeBase()
			holder.transform = matrix.translate(
				new geographic(point.lon * trig.rad, point.lat * trig.rad, cPreviewAltitudeKm).cartesian
			)
			dome.transform = matrix.scale(cPreviewPointRadiusKm)
			holder.addNode(dome)
			this.addNode(holder)
		}

		if (points.length >= 2) {
			this.addNode(new PolylineNode(context, {
				positions: densifiedPathPositions(points, cPreviewAltitudeKm, kind === "region" && points.length > 2),
				width: cPreviewLineWidth,
				color: storyPaletteColor("blue", 0.7),
				depthWrite: false,
			}))
		}
	}
}

export class StoryLayer extends DatasetLayer<StoryAnnotationFeature> {
	private unsubscribeRedux?: () => void

	private lastAnnotations?: StoryAnnotation[]
	private lastShownKey = ""
	private lastDrawing: unknown = null
	private previewNode?: StoryDrawingPreviewNode

	constructor(context: EarthBrowserContext) {
		super(context, "story-annotations")
		this.subscribeToRedux()
	}

	private subscribeToRedux(): void {
		const apply = () => {
			const state = engineBridge.getState()
			if (!state) return

			// The story whose annotations are on the globe: playback wins,
			// otherwise the editor's story
			const activeStory = state.dialog.activeStory
			const editorId = state.dialog.storyEditorId
			const storyId = activeStory?.storyId ?? editorId
			const preview = state.dialog.sharedStoryPreview
			const story = storyId
				? state.stories.stories[storyId] ?? (preview?.id === storyId ? preview : undefined)
				: undefined

			// Playback shows the current stop's annotations; the open editor
			// shows everything (unless a playback preview is running)
			const editing = editorId !== null && editorId === storyId && !activeStory
			const activeStop = story && activeStory ? story.stops[activeStory.stopIndex] : undefined
			const shownIds = new Set(editing
				? (story?.annotations.map(a => a.id) ?? [])
				: (activeStop?.annotationIds ?? []))
			const shownKey = `${storyId ?? ""}:${[...shownIds].sort().join(",")}`

			const annotations = story?.annotations
			if (annotations !== this.lastAnnotations || shownKey !== this.lastShownKey) {
				this.lastAnnotations = annotations
				this.lastShownKey = shownKey
				this.updateSceneObjects((annotations ?? []).map(annotation => ({
					id: annotation.id,
					annotation,
					shown: shownIds.has(annotation.id),
				})))
			}

			// Click-to-draw preview (immer replaces the object on each point)
			const drawing = state.dialog.storyDrawing
			if (drawing !== this.lastDrawing) {
				this.lastDrawing = drawing
				if (this.previewNode) {
					this.removeNode(this.previewNode)
					this.previewNode.destroy()
					this.previewNode = undefined
				}
				if (drawing && drawing.points.length > 0) {
					this.previewNode = new StoryDrawingPreviewNode(this.context, drawing.kind, drawing.points)
					this.addNode(this.previewNode)
				}
			}
		}

		apply()
		this.unsubscribeRedux = engineBridge.subscribe(apply)
	}

	public createSceneObject(feature: StoryAnnotationFeature): DatasetSceneObject<StoryAnnotationFeature> | undefined {
		return new StoryAnnotationNode(this.context, feature)
	}

	/** Redux-fed: the base refresh queries the dataset DB, which stories
	 *  don't live in */
	public override async refresh(): Promise<void> {}

	override destroy(): void {
		this.unsubscribeRedux?.()
		super.destroy()
	}
}
